// @givmo/crm — the Add contact dialog.
//
// Adds one of the organization's own contacts: name, email, phone, postal address, tags, notes.
// `source` is server-stamped and `external_ref` / the import reference belong to the importer, so
// neither is offered here. An existing contact is read, edited and deleted in its panel
// (`contact-sheet.tsx`), which opens from its row.
//
// NO MATCHING, EVER. This form does not look up, suggest, autocomplete, or link a Givmo donor — not
// even when the email typed here equals a feed identity. The two row modes stay structurally disjoint.
//
// TERMS GATE: creating requires the class profile's operative processing instrument. The backend
// answers 409 otherwise; the host may supply the action link and its profile words.

import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Button,
  Dialog,
  ErrorMessage,
  TextField,
  Textarea,
  testIdProp,
  toast,
} from '@givmo/design-system';
import type { CrmModuleConfig } from './crm-config';
import { createCrmImportedContact } from './crm-transport';
import { isTermsRequired, serverMessage } from './crm-decode';

const PREFIX = 'crm-contact';

/** The editable shape. Tags are entered as a comma-separated list and normalized on submit. */
export interface ImportedContactDraft {
  displayName: string;
  email: string;
  phone: string;
  postalAddress: string;
  tags: string;
  notes: string;
}

const EMPTY_DRAFT: ImportedContactDraft = {
  displayName: '',
  email: '',
  phone: '',
  postalAddress: '',
  tags: '',
  notes: '',
};

/** Split the comma-separated tag entry into trimmed, de-duplicated, non-empty labels. */
export function parseTags(raw: string): string[] {
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const part of raw.split(',')) {
    const label = part.trim().replace(/\s+/g, ' ');
    if (label.length === 0) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    tags.push(label);
  }
  return tags;
}

/** A contact needs something to identify it — the backend requires a name or an email (422 otherwise). */
export function validateDraft(draft: ImportedContactDraft): string | null {
  if (draft.displayName.trim().length === 0 && draft.email.trim().length === 0) {
    return 'Add a name or an email so you can find this contact later.';
  }
  return null;
}

/** An optional wire field: a blank entry is sent as null so it clears rather than storing "". */
function optionalField(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export interface ImportedContactDialogProps {
  config: CrmModuleConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function ImportedContactDialog({
  config,
  open,
  onOpenChange,
  onSaved,
}: ImportedContactDialogProps) {
  const [draft, setDraft] = useState<ImportedContactDraft>(EMPTY_DRAFT);
  const [formError, setFormError] = useState<string | null>(null);
  const [termsRequired, setTermsRequired] = useState(false);
  const [pending, setPending] = useState(false);

  // Every opening starts empty.
  useEffect(() => {
    if (!open) return;
    setDraft(EMPTY_DRAFT);
    setFormError(null);
    setTermsRequired(false);
  }, [open]);

  const showTermsLink = config.links.terms !== undefined;

  const field = (key: keyof ImportedContactDraft) => (value: string) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const submit = async () => {
    const invalid = validateDraft(draft);
    if (invalid !== null) {
      setFormError(invalid);
      return;
    }
    setFormError(null);
    setTermsRequired(false);
    setPending(true);

    const body = {
      display_name: optionalField(draft.displayName),
      email: optionalField(draft.email),
      phone: optionalField(draft.phone),
      postal_address: optionalField(draft.postalAddress),
      tags: parseTags(draft.tags),
      notes: optionalField(draft.notes),
    };

    try {
      await createCrmImportedContact(config.orgRef, body);
      toast.success('Contact added');
      onSaved();
      onOpenChange(false);
    } catch (error) {
      // The terms gate is the one refusal worth explaining in place rather than as a passing toast:
      // it is actionable, and the action lives on another screen. Everything else is just the message.
      setTermsRequired(isTermsRequired(error));
      setFormError(serverMessage(error, 'We couldn’t save that contact. Please try again.'));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <Dialog.Content
        aria-label="Add contact"
        className="flex w-[520px] flex-col gap-16"
        testId={`${PREFIX}-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Add contact
        </Dialog.Title>
        <p className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
          {config.vocab.suppliedSegregationNote}
        </p>

        <form
          className="flex flex-col gap-12"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          {...testIdProp(`${PREFIX}-form`)}
        >
          <TextField
            label="Name"
            value={draft.displayName}
            onChange={(event) => field('displayName')(event.target.value)}
            autoComplete="off"
            testId={`${PREFIX}-name`}
          />
          <TextField
            label="Email"
            type="email"
            value={draft.email}
            onChange={(event) => field('email')(event.target.value)}
            autoComplete="off"
            testId={`${PREFIX}-email`}
          />
          <TextField
            label="Phone"
            value={draft.phone}
            onChange={(event) => field('phone')(event.target.value)}
            autoComplete="off"
            testId={`${PREFIX}-phone`}
          />
          <TextField
            label="Address"
            value={draft.postalAddress}
            onChange={(event) => field('postalAddress')(event.target.value)}
            autoComplete="off"
            testId={`${PREFIX}-address`}
          />
          <TextField
            label="Tags"
            placeholder="Separate tags with commas"
            value={draft.tags}
            onChange={(event) => field('tags')(event.target.value)}
            autoComplete="off"
            testId={`${PREFIX}-tags`}
          />
          <Textarea
            label="Notes"
            rows={3}
            value={draft.notes}
            onChange={(event) => field('notes')(event.target.value)}
            testId={`${PREFIX}-notes`}
          />

          {formError !== null ? (
            <div className="flex flex-col gap-6" {...testIdProp(`${PREFIX}-error`)}>
              <ErrorMessage>{formError}</ErrorMessage>
              {termsRequired && showTermsLink ? (
                <Link
                  to={config.links.terms}
                  className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 underline underline-offset-2"
                  {...testIdProp(`${PREFIX}-terms-link`)}
                >
                  {config.vocab.termsActionLabel}
                </Link>
              ) : null}
            </div>
          ) : null}

          <div className="mt-8 flex items-center justify-end gap-8">
            <Dialog.Close
              disabled={pending}
              render={<Button appearance="saas" tone="secondary" size="md" type="button" />}
              {...testIdProp(`${PREFIX}-cancel`)}
            >
              Cancel
            </Dialog.Close>
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              type="submit"
              loading={pending}
              testId={`${PREFIX}-submit`}
            >
              Add contact
            </Button>
          </div>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}
